import { create } from "zustand";
import { useEffect } from "react";
import { api, currentTokens, setSignOutHandler, storeTokens } from "@shared/api";
import { realtime } from "@shared/realtime";
import { forgetPlace } from "@shared/lib/remember";
import type { SelfUser } from "@shared/api";

interface SessionValue {
  me: SelfUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
  patchMe: (next: SelfUser) => void;
}

/**
 * Сессия.
 *
 * Стор, а не контекст: провайдер здесь ничего не давал сверх состояния, а
 * читать сессию вне отрисовки — обычное дело (сокет, обработчик выхода).
 * Подписка компонента идёт через тот же `useSession`, поэтому места вызова не
 * изменились.
 */
export const useSession = create<SessionValue>()((set) => ({
  me: null,
  loading: true,

  signIn: async (email, password) => {
    storeTokens(await api.auth.login(email, password));
    set({ me: await api.users.me() });
  },

  signOut: async () => {
    try {
      await api.account.logout();
    } catch {
      // Выход из своей сессии — местное дело: если сервер не ответил,
      // токены всё равно нужно выбросить.
    }
    storeTokens(null);
    realtime.disconnect();
    // Место — такая же чужая вещь, как токены: см. `forgetPlace`.
    forgetPlace();
    set({ me: null });
  },

  refreshMe: async () => {
    if (!currentTokens().access) {
      set({ me: null, loading: false });
      return;
    }

    try {
      set({ me: await api.users.me() });
    } catch {
      set({ me: null });
    } finally {
      set({ loading: false });
    }
  },

  /*
   * Поверх прежнего, а не вместо: правка профиля и смена аватара отвечают
   * публичным профилем, а в нём нет почты и срока блокировки. Заменой `me`
   * терял бы их после каждого «Сохранить», и раздел «Учётная запись»
   * показывал бы пустую почту.
   */
  patchMe: (next) => set((state) => ({ me: state.me ? { ...state.me, ...next } : next })),
}));

/** Сессия, когда вход уже произошёл: избавляет экраны от проверок на null. */
export function useMe(): SelfUser {
  const me = useSession((state) => state.me);
  if (!me) throw new Error("useMe до входа");
  return me;
}

/**
 * Заводит сессию: подтягивает себя и держит сокет при живом входе.
 *
 * Вызывается один раз в корне. Прежде это делал провайдер; провайдер исчез
 * вместе с контекстом, а работа осталась — она про побочные действия, а не
 * про хранение.
 */
export function useSessionBoot() {
  const meId = useSession((state) => state.me?.id);

  useEffect(() => {
    // Сессия могла кончиться посреди работы — тогда клиент уже сбросил
    // токены, а нам остаётся показать вход.
    setSignOutHandler(() => {
      useSession.setState({ me: null });
      realtime.disconnect();
    });

    void useSession.getState().refreshMe();
  }, []);

  useEffect(() => {
    if (!meId) return;
    void realtime.connect();
    return () => realtime.disconnect();
  }, [meId]);
}
